import { useEffect, useRef } from "react";
import { aboutImage, aboutImage2, aboutVideo } from "@/mocks/homeData";

export default function AboutUs() {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(video);

    return () => observer.disconnect();
  }, []);

  return (
    <section id="nosotros" className="pt-8 md:pt-12 pb-16 md:pb-24 px-4 md:px-8 lg:px-12 bg-background-50">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 lg:gap-16 items-stretch">
        {/* Images */}
        <div className="reveal order-2 md:order-1 flex flex-col gap-5">
          <div className="relative rounded-2xl overflow-hidden group flex-1 min-h-64 md:min-h-72">
            <img
              src={aboutImage}
              alt="Equipo de ingenieros SERIN Ingeniería de Proyectos SAS"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/40 to-transparent" />
          </div>

          <div className="relative rounded-2xl overflow-hidden group flex-1 min-h-64 md:min-h-72 bg-background-100">
            <video
              ref={videoRef}
              src={aboutVideo}
              poster={aboutImage2}
              muted
              loop
              playsInline
              preload="metadata"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Content */}
        <div className="reveal order-1 md:order-2">
          <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-800 text-xs font-bold tracking-wide uppercase mb-4">
            Nosotros
          </span>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
            35 años creando soluciones para climatización y refrigeración
          </h2>
          <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-4">
            En <strong className="text-foreground-900">SERIN Ingeniería de Proyectos S.A.S.</strong> diseñamos, instalamos y mantenemos soluciones de climatización, refrigeración y ventilación para empresas, comercios e industrias en Colombia.
          </p>
          <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-6">
            Combinamos experiencia, ingeniería y tecnología para desarrollar sistemas eficientes, confiables y adaptados a las necesidades de cada proyecto.
          </p>
          <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-6">
            <strong className="text-foreground-900">Serin</strong> y <strong className="text-foreground-900">Organización Serin</strong> son marcas registradas de SERIN Ingeniería de Proyectos S.A.S.
          </p>

          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                <i className="ri-award-line text-primary-600 text-lg w-5 h-5 flex items-center justify-center" />
              </div>
              <div>
                <div className="font-heading font-bold text-foreground-950 text-sm">Certificados</div>
                <div className="text-foreground-500 text-xs">Normas SST</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                <i className="ri-map-pin-line text-primary-600 text-lg w-5 h-5 flex items-center justify-center" />
              </div>
              <div>
                <div className="font-heading font-bold text-foreground-950 text-sm">Bogotá, Colombia</div>
                <div className="text-foreground-500 text-xs">Cobertura nacional</div>
              </div>
            </div>
          </div>

          <div className="space-y-4 mb-8">
            <p className="font-heading text-lg md:text-xl font-bold text-foreground-950">3 razones para elegir SERIN</p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                  <i className="ri-settings-3-line text-accent-600 text-lg w-5 h-5 flex items-center justify-center" />
                </div>
                <div>
                  <div className="font-heading font-bold text-foreground-950 text-sm">Ingeniería especializada</div>
                  <div className="text-foreground-500 text-sm leading-relaxed">Soluciones diseñadas según las necesidades técnicas de cada proyecto.</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                  <i className="ri-flashlight-line text-accent-600 text-lg w-5 h-5 flex items-center justify-center" />
                </div>
                <div>
                  <div className="font-heading font-bold text-foreground-950 text-sm">Eficiencia energética</div>
                  <div className="text-foreground-500 text-sm leading-relaxed">Tecnología orientada a optimizar el consumo y el rendimiento de los sistemas.</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                  <i className="ri-tools-line text-accent-600 text-lg w-5 h-5 flex items-center justify-center" />
                </div>
                <div>
                  <div className="font-heading font-bold text-foreground-950 text-sm">Acompañamiento integral</div>
                  <div className="text-foreground-500 text-sm leading-relaxed">Instalación, mantenimiento y soporte técnico para una operación confiable.</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center gap-3">
            <p className="font-heading text-xl md:text-2xl font-bold text-foreground-950">¿Tienes un proyecto en mente?</p>
            <a
              href="https://wa.link/0adpkz"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent-500 text-background-50 font-semibold text-sm hover:bg-accent-600 transition-colors whitespace-nowrap shadow-lg shadow-accent-500/20"
            >
              Hable con nuestros especialistas
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}